import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import {
    OmitIndexed,
    RequiredKeepUndefined,
    TypeOrTypeArray,
    UnionToIntersection,
} from "@toolkit/Framework/Types/Common";

type TrackEventBuildParameters<
    PartialProps extends keyof TrackEventParameters<Item, Model> | null,
    RequiredProps extends keyof TrackEventParameters<Item, Model> | null,
    Item,
    Model,
> = Toolkit.GoogleAnalytics.Any<
    UnionToIntersection<
        PartialProps extends keyof TrackEventParameters<Item, Model>
            ? RequiredProps extends keyof TrackEventParameters<Item, Model>
                ? Pick<Partial<TrackEventParameters<Item, Model>>, PartialProps> &
                      Pick<RequiredKeepUndefined<TrackEventParameters<Item, Model>>, RequiredProps>
                : Pick<Partial<TrackEventParameters<Item, Model>>, PartialProps>
            : RequiredProps extends keyof TrackEventParameters<Item, Model>
            ? Pick<RequiredKeepUndefined<TrackEventParameters<Item, Model>>, RequiredProps>
            : TrackEventParameters<Item, Model>
    >
>;

export interface TrackEventParameters<
    Item = Toolkit.GoogleAnalytics.AnyItem,
    Model = Toolkit.GoogleAnalytics.MapperModel,
> extends OmitIndexed<Toolkit.GoogleAnalytics.AnyEvent, "items"> {
    action: Toolkit.GoogleAnalytics.AnyEventName;
    items?: Item[] | TypeOrTypeArray<Model> | undefined | null;
}

export type TrackEventBaseParameters<
    PartialProps extends keyof TrackEventParameters<Item, Model> | null = null,
    RequiredProps extends keyof TrackEventParameters<Item, Model> | null = null,
    Item = Toolkit.GoogleAnalytics.AnyItem,
    Model = Toolkit.GoogleAnalytics.MapperModel,
> = TrackEventBuildParameters<
    "action" | "page_type" | (PartialProps extends null ? never : PartialProps),
    RequiredProps,
    Item,
    Model
>;

const trackEvent = <T extends TrackEventParameters<any, any>>(
    parameters: T,
    options?: Toolkit.GoogleAnalytics.Options,
) => {
    if (!ga.enabled) {
        return {};
    }

    if (!parameters.items) {
        delete parameters.items;
    }

    const { action, items, value, ...otherParameters } = ga.util.extendTrackEventParameters(
        options?.skipOptionMapper ? parameters : { ...ga.map.options(options), ...parameters },
        options,
    );

    const mappedItems = ga.map.items(items, options);
    const data: Toolkit.GoogleAnalytics.AnyEvent = {
        ...otherParameters,
        items: mappedItems,
        value: typeof value === "undefined" ? ga.util.getItemsValue(mappedItems, options) : value,
    };

    if (!options?.skipTracking) {
        if (!options?.requireItems || data.items?.length) {
            ga.push("event", action, data);
        } else {
            ga.util.logDebug(`"${action}" event skipped, the "Items" property is missing or empty:`, data);
        }
    }

    return data;
};

export default trackEvent;
